import { faFileExport } from '@fortawesome/pro-regular-svg-icons'
import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IWButton from 'shared/components/thunderbolt/IWButton'
import IWGenericTable from 'shared/components/thunderbolt/IWGenericTable'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import IWTypography from 'shared/components/thunderbolt/IWTypography'
import { TIMEZONES } from 'shared/constants'
import { downloadZip, getUniqueProperty } from 'shared/helpers'
import { convertIbtToISOFormat } from 'shared/loadSchedulingClient'
import { WizardStepProps } from 'shared/types'
import styled from 'styled-components'
import { formatToIbtConvertPayload } from '../helpers'
import { lsInternalBilateralTableSchema } from '../schemas'
import { IbtCsvRowItem } from '../types'

const StyledFilterButton = styled(IWButton)`
  width: fit-content;
`

const StyledActionableSection = styled.div`
  display: flex;
  flex-direction: column;

  & > :first-child {
    margin-bottom: 0.25rem;
  }
`

const TOAST_DURATION = 30_000

const tableColumns = lsInternalBilateralTableSchema

type TableItem = IbtCsvRowItem

type StepState = {
  internalBilaterals: IbtCsvRowItem[]
}

export type Props = WizardStepProps<StepState>

export default function LSSubmissionPreviewIBT({ state, onChange }: Props) {
  const { t } = useTranslation()
  const toast = useToast()
  const internalBilaterals = state?.internalBilaterals || []
  const currentMarket = getUniqueProperty('market', internalBilaterals)
  const [items, setItems] = useState<TableItem[]>([])
  const [updatedTableItems, setUpdatedTableItems] = useState<TableItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isConverting, setIsConverting] = useState(false)

  const [showConvertButton] = useState(() => {
    if (!currentMarket) {
      return false
    }

    const market = currentMarket.toUpperCase()

    const markets = Object.keys(TIMEZONES).filter(
      (k) => k !== 'ERCOT' && k !== 'MISO' && k !== 'PJM',
    )

    return markets.includes(market.toUpperCase())
  })

  const toTableItems = (ibtItems: IbtCsvRowItem[]) => {
    const orderedItems = orderBy(ibtItems || [], [
      ['market', 'brand', 'zone', 'date', 'buyer', 'seller'],
    ])
    return orderedItems as TableItem[]
  }

  const handleConvert = async () => {
    setIsConverting(true)

    const toastId = toast.grey(t('loadScheduling.toasts.converting'), {
      duration: TOAST_DURATION,
    })

    try {
      if (currentMarket === undefined) {
        return
      }

      const ibts =
        currentMarket === 'ERCOT' || currentMarket === 'PJM'
          ? updatedTableItems
          : updatedTableItems.map((v) => ({
              ...v,
              contract_id: v.contract_name,
            }))

      const payload = formatToIbtConvertPayload(ibts as IbtCsvRowItem[])

      const response = await convertIbtToISOFormat(payload)

      const filename = `ls-ibt-files-${Date.now()}`
      await downloadZip(response, filename, false)
    } catch (error) {
      toast.alert(t('loadScheduling.toasts.conversionError'))
    } finally {
      toast.removeToast(toastId)
      setIsConverting(false)
    }
  }

  const renderTopRightComponent = () => {
    if (!showConvertButton) {
      return
    }

    return (
      <StyledFilterButton
        disabled={isConverting}
        onClick={() => handleConvert()}
        icon={faFileExport}
        iconPosition="leading"
      >
        {t('button.convert')}
      </StyledFilterButton>
    )
  }

  useEffect(() => {
    if (internalBilaterals?.length) {
      setItems(toTableItems(internalBilaterals))
      setIsLoading(false)
    }
  }, [internalBilaterals])

  useEffect(() => {
    const allowSubmit =
      currentMarket !== undefined &&
      (currentMarket === 'ERCOT' || currentMarket === 'PJM')

    onChange(
      {
        internalBilaterals: updatedTableItems as IbtCsvRowItem[],
      },
      allowSubmit,
    )
  }, [updatedTableItems])

  return (
    <>
      <StyledActionableSection>
        <IWTypography
          size="lg"
          weight="medium"
          fontHue={{ color: 'grey', value: 900 }}
        >
          {t('loadScheduling.titles.submissionPreview')}
        </IWTypography>

        <IWTypography fontHue={{ color: 'grey', value: 500 }} size="sm">
          {t('loadScheduling.descriptions.submissionPreview')}
        </IWTypography>
      </StyledActionableSection>

      {isLoading && <IWLoading />}

      {Boolean(internalBilaterals?.length) && !isLoading && (
        <IWGenericTable
          border
          items={items}
          columns={tableColumns}
          pinnedColumns={{ brand: 'left' }}
          showColumnSettings
          onTableItemsUpdated={(updatedItems) => {
            setUpdatedTableItems(updatedItems)
          }}
          topRightComponent={renderTopRightComponent()}
        />
      )}
    </>
  )
}
